import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { BaseParamsDto, InsuranceParamsDto, PolicyHolderDto, VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'
import { FindVehicle, InitialBaseParamsAddonValues, InitialGeneralParamsValues, InitialPolicyHolderValues, InitialVehicleValues } from 'BrokerTsLib/src/dtos'
import { DummyStep1Addon } from 'BrokerTsLib/src/dtos/DummyStep1'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { Controller, FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { IsOtherNonregistered, IsTrailer, PolicyHolderValidationSchema } from 'BrokerTsLib/src/validation'
import { VehicleExtraBaseParamsValidationSchema, VehicleValidationSchema } from 'BrokerTsLib/src/validation/vehicle-extra'
import { format } from 'date-fns'
import _ from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'

import { CheckboxFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { Datepicker } from '../../components/Datepicker'
import ErrorSummary from '../../components/ErrorSummary'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { InputGroup } from '../../components/InputGroup'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { MainContent } from '../../components/MainContent'
import { Panel } from '../../components/Panel'
import { RadioStack } from '../../components/RadioStack'
import { useInsurers, useSelections } from '../../hooks'
import { useApp } from '../../hooks/useApp'
import { useNameof } from '../../hooks/useNameof'
import { useGetMakers, useGetModels, useGetModelsBase } from '../../hooks/useVehicle'
import { Check } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { BCNOptions } from '../BCNOptions'
import { PriceExcludeVatWarning } from '../carkulka/PriceExcludeVatWarning'
import { transformGeneralParamsDto, transformVehicleDto } from '../carkulka/Step1_transforms'
import { transformInsuranceParamsToFormValues } from '../carkulka/Step1Form'
import { VehicleIdentificationForm } from '../carkulka/VehicleIdentificationForm'
import { FormButtons } from '../FormButtons'
import { ICOOptions } from '../ICOOptions'
import { InsureeAddressRuian } from '../InsureeAddressRuian'
import { InsureeName } from '../InsureeName'
import { InsureeType } from '../InsureeType'

interface Step1FormProps {
  onSubmit: (values: InsuranceParamsDto) => Promise<any>,
  data?: InsuranceParamsDto
}

export const schema = yup.object().shape(
  {
    baseParams: VehicleExtraBaseParamsValidationSchema,
    vehicle: VehicleValidationSchema,
    policyHolder: PolicyHolderValidationSchema,
  }
)

const defaultValues: InsuranceParamsDto = {
  nextPage: 'next',
  baseParams: InitialBaseParamsAddonValues,
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  vehicle: {
    ...InitialVehicleValues,
    manufacturerWarrantyType: undefined,
    // výchozí hodnoty, které jsou požadovány validátorem, ale pro doplňky nejou potřeba
    puvod: Selection.VehicleOriginCzech,
    uzemniPlatnost: Selection.TerritorialValidityEurope,
    usage: Selection.NormalUsage
  }
}

export const Step1Form = ({ onSubmit, data = defaultValues }: Step1FormProps) => {
  const { environment } = useApp()
  const { triggerNewInsurance, clearTriggerNewInsurance, insuranceParamsId } = useContext(insuranceParamsContext)
  const { data: dataPt, isLoading: isLoadingPt, isFetching: isFetchingPt } = useSelections(SelectionType.ZpusobPlatby)
  const { data: dataDruh, isLoading: isLoadingDruh, isFetching: isFetchingDruh } = useSelections(SelectionType.DruhVozidla)
  const { data: dataPalivo, isLoading: isLoadingPalivo, isFetching: isFetchingPalivo } = useSelections(SelectionType.Palivo)
  const { data: dataWarrantyLimitKm, isLoading: isLoadingWarrantyLimitKm, isFetching: isFetchingWarrantyLimitKm } = useSelections(SelectionType.WarrantyLimitKm)
  const { data: dataWarrantyMonthLimit, isLoading: isLoadingWarrantyMonthLimit, isFetching: isFetchingWarrantyMonthLimit } = useSelections(SelectionType.WarrantyMonthLimit)
  const { data: dataUsage, isLoading: isLoadingUsage, isFetching: isFetchingUsage } = useSelections(SelectionType.PouzitiVozidla)
  const { data: dataVybava, isLoading: isLoadingVybava, isFetching: isFetchingVybava } = useSelections(SelectionType.Vybava)
  const { data: allAddonInsurersData, isLoading: isLoadingAddonInsurers, isFetching: isFetchingAddonInsurers } = useInsurers(true, null)

  const nameof = useNameof<InsuranceParamsDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof(x => x.policyHolder))
  const bpnameof = useNameof<BaseParamsDto>(nameof(x => x.baseParams))

  const formValues = useMemo(() => transformInsuranceParamsToFormValues(data), [data])
  useEffect(() => {
    methods.reset(formValues)
  }, [formValues])

  const isContracted = !!formValues.generalParams.contractNumber

  const methods = useForm<InsuranceParamsDto>({
    defaultValues: formValues,
    resolver: yupResolver(schema) as any,
    mode: 'onBlur'
  })
  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    watch,
    reset,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = methods

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [vatWarningResetKey, setVatWarningResetKey] = useState(0)

  const submitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.vehicle.firstRegistrationDate = values.vehicle.firstRegistrationDate ? format(new Date(values.vehicle.firstRegistrationDate as any), 'yyyy-MM-dd') as any : undefined
    values.generalParams.insuranceStartDate = values.generalParams.insuranceStartDate ? format(new Date(values.generalParams.insuranceStartDate as any), 'yyyy-MM-dd') as any : undefined
    values.vehicle.purchaseDate = values.vehicle.purchaseDate ? format(new Date(values.vehicle.purchaseDate as any), 'yyyy-MM-dd') as any : undefined
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    if (values.policyHolder.owner) values.policyHolder.owner.birthday = formatDate(values.policyHolder.owner.birthday)
    if (values.policyHolder.holder) values.policyHolder.holder.birthday = formatDate(values.policyHolder.holder.birthday)

    // ten jejich RadioIcon a select neumi s intem
    values.vehicle.druh = values.vehicle.druh ? parseInt(values.vehicle.druh.toString()) as any : undefined
    values.vehicle.palivo = values.vehicle.palivo ? parseInt(values.vehicle.palivo.toString()) as any : undefined
    values.vehicle.usage = values.vehicle.usage ? parseInt(values.vehicle.usage.toString()) as any : undefined

    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current')
          savedFormAlertOnOpen()
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const useVinonline = (vinOnlineData: VinOnlineVehicleDto) => {
    const veh = new FindVehicle(vinOnlineData)
    // pokud davam setValue cely objekt vehicle, tak se neprerendruje komponenta a nezmeni values - v UI to vypada dobre, ale uz se nespusti refetch apod.
    setValue('vehicle.licensePlate', veh.licensePlate)
    setValue('vehicle.vin', veh.vin)
    setValue('vehicle.palivo', veh.palivo)
    setValue('vehicle.puvod', veh.puvod)
    setValue('vehicle.objem', veh.objem)
    setValue('vehicle.vykon1', veh.vykon1)
    setValue('vehicle.pocetMistSezeni', veh.pocetMistSezeni)
    setValue('vehicle.celkovaHmotnost', veh.celkovaHmotnost)
    setValue('vehicle.cisloTechnickehoPrukazu', veh.cisloTechnickehoPrukazu)
    setValue('vehicle.firstRegistrationDate', veh.firstRegistrationDate)
    setValue('vehicle.firstRegistrationDateCz', veh.firstRegistrationDateCz)

    setValue('vehicle.znacka', veh.znacka)
    setValue('vehicle.druh', veh.druh)

    // bud se nastavuje model nebo zakladni model
    if (veh.model) {
      setValue('vehicle.zakladniModel', '')
      setValue('vehicle.model', veh.model)
    }
    else {
      setValue('vehicle.model', '')
      setValue('vehicle.zakladniModel', veh.zakladniModel)
    }
  }

  const holderSameAs = watch('policyHolder.holderSameAs')
  const ownerSameAs = watch('policyHolder.ownerSameAs')
  const phType = watch('policyHolder.person.type')
  const ownerType = watch('policyHolder.owner.type')
  const holderType = watch('policyHolder.holder.type')
  const priceType = watch('vehicle.priceType')
  const fuel = watch('vehicle.palivo')
  const category = watch('vehicle.druh')
  const isPrivesNaves = IsTrailer(category?.toString())
  const isOtherNonregistered = IsOtherNonregistered(category?.toString())
  const maker = watch('vehicle.znacka')
  const modelBase = watch('vehicle.zakladniModel')
  const model = watch('vehicle.model')
  const manufacturerWarranty = watch('vehicle.manufacturerWarrantyType')

  const selectedAddonInsurers = watch('baseParams.addonInsurers') ?? []

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance) {
      reset(transformInsuranceParamsToFormValues(defaultValues))
      setVatWarningResetKey(k => k + 1)
    }

    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  const ownerOptions = useMemo(() => [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    { label: 'Stejný s držitelem/provozovatelem', value: Selection.PersonSameAsKeeper },
    { label: 'Jiný', value: '' },
  ], [holderSameAs])

  const onMfctWarrantyTypeChange = (e: string) => {
    setValue('vehicle.manufacturerWarrantyType', parseInt(e))

    if (e == '1') {
      if (!getValues('vehicle.warrantyStartDate'))
        setValue('vehicle.warrantyStartDate', getValues('vehicle.firstRegistrationDate'))
    }
  }

  const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('vehicle.druh', e.target.value as any)
    setValue('vehicle.znacka', '')
    setValue('vehicle.znackaText', '')
  }

  const allAddonInsurers = useMemo(() => [...(allAddonInsurersData ?? [])], [
    allAddonInsurersData,
    methods.formState.defaultValues
  ])

  const { refetch: refetchMakers, data: makersData, isLoading: isLoadingMakers, isFetching: isFetchingMakers } = useGetMakers({ fuelId: fuel ?? 0, categoryId: category ?? 0, useBrokerMakeModel: false }, { enabled: false })

  useEffect(() => {
    const data = makersData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(maker?.toUpperCase() ?? ''))
      setValue('vehicle.znacka', '')
  }, [makersData])

  const { refetch: refetchModelBase, data: modelBaseData, isLoading: isLoadingModelsBase, isFetching: isFetchingModelsBase } = useGetModelsBase({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: maker ?? '', useBrokerMakeModel: false }, { enabled: false })

  useEffect(() => {
    const data = modelBaseData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(modelBase?.toUpperCase() ?? ''))
      setValue('vehicle.zakladniModel', '')
  }, [modelBaseData])

  const { refetch: refetchModels, data: modelsData, isLoading: isLoadingModels, isFetching: isFetchingModels } = useGetModels({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: maker ?? '', modelBase: modelBase, useBrokerMakeModel: false }, { enabled: false })

  useEffect(() => {
    const data = modelsData?.data
    if (data && !data.map((x: any) => x.toUpperCase()).includes(model?.toUpperCase() ?? ''))
      setValue('vehicle.model', '')
  }, [modelsData])

  useEffect(() => {
    refetchMakers()
      .then(() => {
        if (maker)
          refetchModelBase().then(() => refetchModels())
      })
  }, [fuel, category])
  useEffect(() => { refetchModelBase(); refetchModels() }, [maker?.toUpperCase()])
  useEffect(() => {
    refetchModels()
  }, [modelBase?.toUpperCase()])

  const fillDummyData = () => {
    const dummy = { ...DummyStep1Addon } // kopie globalniho objektu.

    dummy.vehicle = transformVehicleDto(DummyStep1Addon.vehicle)
    dummy.generalParams = transformGeneralParamsDto(DummyStep1Addon.generalParams)

    reset(dummy)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle display={'flex'} flexDir={'row'}>
                Parametry pojištění
                {environment == 'development' && <Text fontSize='md' paddingX={2} onClick={fillDummyData}>*</Text>}
              </FormSectionTitle>

              <FormSectionDescription>Zadejte základní parametry pojištění.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              {/* generalParams.name */}
              <FormControl isInvalid={!!errors.generalParams?.name} isDisabled={isContracted}>
                <FormLabel htmlFor="generalParams.name" tooltip="Zadejte název nabídky">Název nabídky</FormLabel>
                <InputGroup
                  id="generalParams.name"
                  placeholder="Zadejte název nabídky"
                  htmlSize={32}
                  errors={errors.generalParams?.name}
                  {...register('generalParams.name', {
                    required: 'Zadejte název nabídky',
                  })}
                />
              </FormControl>

              <FormSpacer />

              <FormGroup size="lg">
                {/* generalParams.insuranceStartDate */}
                <FormControl isInvalid={!!errors.generalParams?.insuranceStartDate}>
                  <FormLabel htmlFor="generalParams.insuranceStartDate" tooltip="Vyberte datum počátku pojištění. Zpětné pojištění není možné.">Datum počátku pojištění</FormLabel>
                  <Datepicker
                    isDisabled={isContracted}
                    errors={errors.generalParams?.insuranceStartDate}
                    date={watch('generalParams.insuranceStartDate')}
                    onDateChange={(date) => setValue('generalParams.insuranceStartDate', date)}
                    dateMin={new Date(new Date().toDateString())}
                  />
                </FormControl>

                {/* generalParams.paymentType */}
                <FormControl>
                  <Stack direction={'row'}>
                    <FormLabel htmlFor="generalParams.paymentType" tooltip="Vyberte způsob platby.">Způsob platby</FormLabel>

                    {(isFetchingPt || isLoadingPt) && <Spinner />}
                  </Stack>

                  <Controller
                    name="generalParams.paymentType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                        <Stack direction="column" spacing={4}>
                          {dataPt?.filter(x => x.id == Selection.Bank).map((item) => (
                            <Radio value={item.id.toString()} key={item.id.toString()}>
                              {item.description}
                            </Radio>
                          ))}
                        </Stack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <InsuranceCompanies
                companies={allAddonInsurers}
                title='Doplňkové pojišťovny'
                isLoadingCompanies={(isFetchingAddonInsurers || isLoadingAddonInsurers)}
                propPath={bpnameof(x => x.addonInsurers)}
                isDisabled={isContracted}
                tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění."
              />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o vozidle</FormSectionTitle>

              <FormSectionDescription>
                Využijte automatické vyplnění pomocí SPZ a čísla velkého technického průkazu nebo
                VIN čísla.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <VehicleIdentificationForm onEnterVinData={useVinonline} />

              <FormSpacer />

              {/* <FormControl>
                <FormLabel tooltip="Vyberte typ vozidla podle technického průkazu">
                  Druh vozidla
                </FormLabel>

                <RadioIconGroup control={control} name="vehicleType">
                  <RadioStack variant="tile">
                    <RadioIcon icon="car" value="car" {...register('vehicleType')}>
                      Osobní auto
                    </RadioIcon>
                    <RadioIcon icon="truck" value="truck" {...register('vehicleType')}>
                      Užitkové vozidlo
                    </RadioIcon>
                    <RadioIcon icon="other" value="other" {...register('vehicleType')}>
                      Ostatní
                    </RadioIcon>
                  </RadioStack>
                </RadioIconGroup>
              </FormControl> */}
              <FormControl sx={{ w: '280px' }} isRequired>
                <Stack direction={'row'}>
                  <FormLabel htmlFor="vehicle.druh" tooltip="Vyberte druh pojišťovaného vozidla podle technického průkazu.">
                    Druh vozidla
                  </FormLabel>
                  {(isFetchingDruh || isLoadingDruh) && <Spinner />}
                </Stack>
                <FormHelperText>Vyberte druh vozidla podle technického průkazu</FormHelperText>

                {(dataDruh?.length ?? 0) > 0 &&
                  <Select id="vehicle.druh" {...register('vehicle.druh')} isDisabled={isContracted} onChange={(e) => onCategoryChange(e)}>
                    <option value='' disabled>Vyberte</option>
                    {dataDruh?.map((item) => (
                      <option value={item.id.toString()} key={item.id}>{item.description}</option>
                    ))}
                  </Select>
                }
              </FormControl>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.palivo */}
                <FormControl sx={{ w: '280px' }} isRequired>
                  <Stack direction={'row'}>
                    <FormLabel htmlFor="vehicle.palivo" tooltip="Vyberte druh paliva podle technického průkazu.">Palivo</FormLabel>
                    {(isFetchingPalivo || isLoadingPalivo) && <Spinner />}
                  </Stack>
                  <FormHelperText>Vyberte druh paliva podle technického průkazu</FormHelperText>

                  {(dataPalivo?.length ?? 0) > 0 &&
                    <Select id="vehicle.palivo" {...register('vehicle.palivo')} isDisabled={isContracted}>
                      <option disabled>Vyberte</option>
                      {dataPalivo?.map((item) => (
                        <option value={item.id.toString()} key={item.id}>{item.description}</option>
                      ))}
                    </Select>
                  }
                </FormControl>

                {/* vehicle.znacka */}
                <FormControl sx={{ w: '280px' }} isInvalid={!!errors.vehicle?.znacka} isRequired>
                  <Stack direction={'row'}>
                    <FormLabel htmlFor="vehicle.znacka" tooltip="Vyberte tovární značku vozidla. V případě, že příslušná tovární značka není ve výběru, kontaktujte správce systému s žádostí o její přidání do číselníku.">
                      Tovární značka
                    </FormLabel>
                    {(isLoadingMakers || isFetchingMakers) && <Spinner />}
                  </Stack>

                  <FormHelperText>Zadejte značku podle technického průkazu</FormHelperText>

                  {(makersData?.data.length ?? 0) > 0 &&
                    <Controller
                      name='vehicle.znacka'
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Select id="vehicle.znacka" onChange={(e) => onChange(e.target.value)} isDisabled={isContracted} value={maker?.toUpperCase()}>
                          <option disabled value=''>Vyberte značku vozidla</option>
                          {makersData!.data.map((item) => (
                            <option value={item.toUpperCase()} key={item}>{item.toUpperCase()}</option>
                          ))}
                        </Select>
                      )}
                    />
                  }

                  {(makersData?.data.length ?? 0) == 0 && <Select isDisabled={true} defaultValue=''><option disabled value=''>Vyberte značku</option></Select>}
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.zakladniModel */}
                <FormControl isInvalid={!!errors.vehicle?.zakladniModel}>
                  <Stack direction={'row'}>
                    <FormLabel htmlFor="vehicle.zakladniModel" tooltip="Pomocné pole, které slouží pro snadnější identifikaci modelu vozidla.">
                      Základní model
                    </FormLabel>

                    {(isLoadingModelsBase || isFetchingModelsBase) && <Spinner />}
                  </Stack>

                  {(modelBaseData?.data.length ?? 0) > 0 &&
                    <Select id="vehicle.zakladniModel" {...register('vehicle.zakladniModel')} isDisabled={isContracted || !maker}>
                      <option value="">
                        Vyberte základní model
                      </option>
                      {modelBaseData!.data.map((item) => (
                        <option value={item} key={item}>{item}</option>
                      ))
                      }
                    </Select>
                  }

                  {(modelBaseData?.data.length ?? 0) == 0 && <Select isDisabled={true} defaultValue=''><option disabled value=''>Vyberte základní model</option></Select>}
                </FormControl>

                {/* vehicle.model */}
                <FormControl sx={{ w: '280px' }} isInvalid={!!errors.vehicle?.model} isRequired>
                  <Stack direction={'row'}>
                    <FormLabel htmlFor="vehicle.model"
                      tooltip="Vyberte model vozidla. V případě, že příslušný model vozidla není ve výběru, kontaktujte správce systému s žádostí o jeho přidání do číselníku.">
                      Model
                    </FormLabel>
                    {(isLoadingModels || isFetchingModels) && <Spinner />}
                  </Stack>

                  {(modelsData?.data.length ?? 0) > 0 &&
                    <Select id="vehicle.model" {...register('vehicle.model')} isDisabled={isContracted}>
                      <option disabled value=''>
                        Vyberte model
                      </option>
                      {modelsData!.data.map((item) => (
                        <option value={item} key={item}>{item}</option>
                      ))}
                    </Select>
                  }
                  {(modelsData?.data.length ?? 0) == 0 && <Select isDisabled={true} defaultValue=''><option disabled value=''>Vyberte model</option></Select>}
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* vehicle.objem */}
                <FormControl isInvalid={!!errors.vehicle?.objem}>
                  <FormLabel htmlFor="volume" tooltip="Zadejte zdvihový objem vozidla podle technického průkazu.">
                    Zdvihový objem
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.objem"
                    placeholder="Např. 1589"
                    htmlSize={6}
                    autoComplete="off"
                    inputMode="decimal"
                    errors={errors.vehicle?.objem}
                    rightElement="㎤"
                    {...register('vehicle.objem')}
                  />
                </FormControl>

                {/* vehicle.vykon1 */}

                <FormControl isInvalid={!!errors.vehicle?.vykon1} isRequired>
                  <FormLabel htmlFor="maxPerf" tooltip="Zadejte maximální výkon vozidla podle technického průkazu.">
                    Maximální výkon
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.vykon1"
                    placeholder="Např. 125"
                    htmlSize={6}
                    autoComplete="off"
                    inputMode="decimal"
                    errors={errors.vehicle?.vykon1}
                    rightElement="kW"
                    {...register('vehicle.vykon1')}
                  />
                </FormControl>

                {/* vehicle.vykon2 */}
                {fuel && isIn(parseInt(fuel.toString()), Selection.HybridBE, Selection.HybridDE, Selection.PluginHybridBE, Selection.PluginHybridDE) && <FormControl isInvalid={!!errors.vehicle?.vykon2}>
                  <FormLabel htmlFor="vehicle.vykon2" tooltip="Zadejte maximální výkon druhého (elektrického) motoru podle technického průkazu.">
                    Maximální výkon 2
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.vykon2"
                    placeholder="Např. 200"
                    htmlSize={6}
                    autoComplete="off"
                    inputMode="decimal"
                    errors={errors.vehicle?.vykon2}
                    rightElement="kW"
                    {...register('vehicle.vykon2')}
                  />
                </FormControl>}

                {/* vehicle.celkovaHmotnost */}
                <FormControl isInvalid={!!errors.vehicle?.celkovaHmotnost} isRequired>
                  <FormLabel htmlFor="weight" tooltip="Zadejte největší technicky přípustnou hmotnost, příp. celkovou hmotnost podle technického průkazu.">
                    Největší technicky přípustná hmotnost
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.celkovaHmotnost"
                    placeholder="Např. 1600"
                    htmlSize={6}
                    autoComplete="off"
                    inputMode="decimal"
                    errors={errors.vehicle?.celkovaHmotnost}
                    rightElement="kg"
                    {...register('vehicle.celkovaHmotnost')}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup>
                {/* registrationDate */}
                <FormControl isInvalid={!!errors.vehicle?.firstRegistrationDate} isRequired>
                  <FormLabel htmlFor="vehicle.firstRegistrationDate" tooltip="Zadejte datum první registrace vozidla.">
                    Datum první registrace
                  </FormLabel>

                  <Datepicker
                    isDisabled={isContracted}
                    date={getValues('vehicle.firstRegistrationDate')}
                    errors={errors.vehicle?.firstRegistrationDate}
                    onDateChange={(date) => setValue('vehicle.firstRegistrationDate', date)}
                  />
                </FormControl>

                {selectedAddonInsurers && selectedAddonInsurers.indexOf(Insurer.Fortegra) >= 0 &&
                  <FormControl isInvalid={!!errors.vehicle?.purchaseDate} isRequired>
                    <FormLabel htmlFor="vehicle.purchaseDate" tooltip="Zadejte datum nákupu vozidla.">
                      Datum nákupu vozidla
                    </FormLabel>

                    <Datepicker
                      isDisabled={isContracted}
                      date={getValues('vehicle.purchaseDate')}
                      errors={errors.vehicle?.purchaseDate}
                      onDateChange={(date) => setValue('vehicle.purchaseDate', date)}
                    />
                  </FormControl>
                }

                <FormControl isInvalid={!!errors.vehicle?.mileage}>
                  <FormLabel htmlFor="vehicle.mileage" tooltip="Zadejte stav tachometru">
                    Stav tachometru
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.mileage"
                    placeholder="Např. 68 000"
                    type="number"
                    htmlSize={12}
                    min="0"
                    max="99999999"
                    autoComplete="off"
                    inputMode="numeric"
                    errors={errors.vehicle?.mileage}
                    rightElement="km"
                    {...register('vehicle.mileage')}
                  />
                </FormControl>

                <FormSpacer />

                {selectedAddonInsurers && selectedAddonInsurers.indexOf(Insurer.Fortegra) >= 0 &&
                  <>
                    <FormGroup variant="price">
                      <FormControl>
                        <Stack direction={'row'}>
                          <FormLabel tooltip="Vyberte Ano, pokud je vozidlo v záruce výrobce.">Vozidlo v záruce</FormLabel>
                        </Stack>
                        <Controller
                          name="vehicle.manufacturerWarrantyType"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <RadioGroup value={value?.toString()} isDisabled={isContracted} onChange={(e) => onMfctWarrantyTypeChange(e)}>
                              <RadioStack variant="stackedTile">
                                <Radio value={'1'}>Ano</Radio>
                                <Radio value={'2'}>Ne</Radio>
                                <Radio value={'0'}>Nechci uvést</Radio>
                              </RadioStack>
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    </FormGroup>

                    {manufacturerWarranty == 0 &&
                      <FormGroup>
                        <Text variant="warning">Připojištění prodloužené záruky nebude kalkulováno</Text>
                      </FormGroup>
                    }

                    {manufacturerWarranty == 1 &&
                      <FormGroup>
                        <FormControl isInvalid={!!errors.vehicle?.warrantyStartDate} isRequired>
                          <FormLabel htmlFor="vehicle.warrantyStartDate" tooltip="Zadejte datum počátku záruky vozidla.">
                            Záruka od
                          </FormLabel>

                          <Datepicker
                            isDisabled={isContracted}
                            date={getValues('vehicle.warrantyStartDate')}
                            errors={errors.vehicle?.warrantyStartDate}
                            onDateChange={(date) => setValue('vehicle.warrantyStartDate', date)}
                          />
                        </FormControl>

                        <FormControl isInvalid={!!errors.vehicle?.warrantyTermLimit} isRequired>
                          <FormLabel htmlFor="vehicle.warrantyTermLimit" tooltip="Zadejte délku trvání záruky - počet měsíců">
                            Počet měsíců záruky
                          </FormLabel>
                          {(isFetchingWarrantyMonthLimit || isLoadingWarrantyMonthLimit) && <Spinner />}

                          {(dataWarrantyMonthLimit?.length ?? 0) > 0 &&
                            <Select id="vehicle.warrantyTermLimit" {...register('vehicle.warrantyTermLimit')} isDisabled={isContracted}>
                              <option value='' disabled>Vyberte</option>
                              {dataWarrantyMonthLimit?.map((item) => (
                                <option value={item.value} key={item.id}>{item.description}</option>
                              ))}
                            </Select>
                          }
                        </FormControl>

                        <FormControl isInvalid={!!errors.vehicle?.warrantyKmLimit} isRequired>
                          <FormLabel htmlFor="vehicle.warrantyKmLimit" tooltip="Zadejte délku trvání záruky - počet km">
                            Počet km záruky
                          </FormLabel>
                          {(isFetchingWarrantyLimitKm || isLoadingWarrantyLimitKm) && <Spinner />}

                          {(dataWarrantyLimitKm?.length ?? 0) > 0 &&
                            <Select id="vehicle.warrantyKmLimit" {...register('vehicle.warrantyKmLimit')} isDisabled={isContracted}>
                              <option value='' disabled>Vyberte</option>
                              {dataWarrantyLimitKm?.filter(item => item.name != 'NU').map((item) => (
                                <option value={item.value} key={item.id}>{item.description}</option>
                              ))}
                            </Select>
                          }
                        </FormControl>

                      </FormGroup>
                    }
                  </>
                }
              </FormGroup>

              <FormSpacer />

              {/* Usage */}
              <FormControl isRequired>
                <FormLabel
                  htmlFor="usage"
                  tooltip="Vyberte použití vozidla. V případě jiného použití než „běžné“ se sjednává přirážka k pojistnému."
                >
                  Použití
                  {(isFetchingUsage || isLoadingUsage) && <Spinner />}
                </FormLabel>

                {(dataUsage?.length ?? 0) > 0 && (
                  <Select
                    id="vehicle.usage"
                    {...register('vehicle.usage')}
                    isDisabled={isContracted}
                  >
                    {dataUsage?.map((item) => (
                      <option value={item.id.toString()} key={item.id}>
                        {item.description}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>

              <FormSpacer />

              <FormGroup>
                {/* Cena vozidla */}
                <FormControl isInvalid={!!errors.vehicle?.price}>
                  <FormLabel htmlFor="vehicle.price" tooltip="V případě, že je pojistníkem fyzická osoba zadejte částku vždy s DPH. V případě, že je klient právnická osoba nebo fyzická osoba podnikatel se v případě zadání částky bez DPH automaticky na třetím kroku nastaví, že je klient plátcem DPH. Vždy si důkladně překontrolujte tyto údaje před sjednáním pojistné smlouvy.">
                    Cena vozidla z faktury
                  </FormLabel>
                  <InputGroup
                    isDisabled={isContracted}
                    id="vehicle.price"
                    placeholder="Např. 599 000"
                    type="number"
                    htmlSize={12}
                    min="0"
                    max="999999999"
                    autoComplete="off"
                    step={1}
                    inputMode="numeric"
                    errors={errors.vehicle?.price}
                    {...register('vehicle.price', {
                      onChange: (e) => {
                        setValue('vehicle.price', e.target.value)
                        if (priceType == Selection.VehicleValuation)
                          setValue('vehicle.priceType', Selection.AccordingPolicyHolder)
                      }
                    })}
                  />
                </FormControl>

                {/* vehicle.priceIncludeVat */}
                <FormControl
                  sx={{ display: 'flex', alignItems: 'center', alignSelf: 'end', py: 1 }}
                >
                  <FormLabel htmlFor="vehicle.priceIncludeVat" variant="switch">
                    bez DPH
                  </FormLabel>

                  <SwitchFormControl
                    name="vehicle.priceIncludeVat"
                    control={control}
                    onChange={(v) => {
                      const price = getValues('vehicle.price')
                      if (price) {
                        const priceInt = price ? parseInt(price!.toString()) : 0
                        const resultPrice = Math.round(
                          v ? priceInt * 1.21 : priceInt / 1.21,
                        )
                        setValue('vehicle.price', resultPrice)
                      }
                    }}
                    isDisabled={isContracted}
                  />

                  <FormLabel htmlFor="vehicle.priceIncludeVat" variant="switch">
                    s DPH
                  </FormLabel>
                </FormControl>
              </FormGroup>

              <PriceExcludeVatWarning
                key={vatWarningResetKey}
                isDisabled={isContracted}
                isNewInsurance={!insuranceParamsId}
              />

              <FormSpacer />

              {/* Výbava */}
              {!isPrivesNaves && !isOtherNonregistered && (
                <FormControl w="100%">
                  <Stack direction={'row'}>
                    <FormLabel
                      htmlFor="vehicle.equipment"
                      tooltip="Vyberte dostupnou doplňkovou výbavu vozidla."
                    >
                      Výbava vozidla
                    </FormLabel>
                    {(isFetchingVybava || isLoadingVybava) && <Spinner />}
                  </Stack>

                  <Controller
                    name="vehicle.equipment"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CheckboxGroup
                        onChange={(values) =>
                          onChange(
                            values.map((val) => parseInt(val.toString())),
                          )
                        }
                        value={value ?? []}
                        isDisabled={isContracted}
                      >
                        <SimpleGrid
                          columns={{ sm: 2, md: 3 }}
                          spacingX="10"
                          spacingY="4"
                          w="100%"
                        >
                          {dataVybava?.filter(
                            (item) =>
                              [
                                `${Selection.AutTransmission}`,
                                `${Selection.FourWheelDrive}`,
                              ].some((x) => item.id.toString() === x) ?? true,
                          )
                            .map((item) => (
                              <Checkbox value={item.id} key={item.id}>
                                {item.description}
                              </Checkbox>
                            ))}
                        </SimpleGrid>
                      </CheckboxGroup>
                    )}
                  />
                </FormControl>
              )}
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>Zadejte informace o pojistníkovi.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <InsureeType personPrefix={phnameof(x => x.person)} />

              <FormSpacer />

              {isIn(phType, Selection.Company, Selection.SelfEmployed) &&
                <CheckboxFormControl name='policyHolder.person.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
              }

              {isIn(phType, Selection.Person, Selection.SelfEmployed) &&
                <BCNOptions personPrefix={phnameof(x => x.person)} />
              }

              {isIn(phType, Selection.Company, Selection.SelfEmployed) &&
                <ICOOptions personPrefix={phnameof(x => x.person)} />
              }

              <FormGroup>
                {isIn(phType, Selection.Person, Selection.SelfEmployed) && <InsureeName personPrefix={phnameof(x => x.person)} isDisabled={isContracted} />}
              </FormGroup>

              <InsureeAddressRuian addressPrefix={phnameof(x => x.person.address)} isDisabled={isContracted} />

              <FormSpacer />

              {/* holder */}
              <FormControl isInvalid={!!errors.policyHolder?.holderSameAs}>
                <FormLabel tooltip="Zadejte držitele/provozovatele vozidla zapsaného v technickém průkazu.">
                  Držitel/provozovatel vozidla
                </FormLabel>

                <Controller
                  name='policyHolder.holderSameAs'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                      <RadioStack variant="stackedTile">
                        <Radio value={Selection.PersonSameAsPolicyHolder.toString()}>
                          Stejný s pojistníkem
                        </Radio>
                        <Radio value="">
                          Jiný
                        </Radio>
                      </RadioStack>
                    </RadioGroup>
                  )}
                />
              </FormControl>

              {!watch('policyHolder.holderSameAs') &&
                (
                  <Panel>
                    <InsureeType personPrefix={phnameof(x => x.holder)} />

                    <FormSpacer />

                    {isIn(holderType, Selection.Company, Selection.SelfEmployed) && (
                      <CheckboxFormControl name='policyHolder.holder.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
                    )}

                    {isIn(holderType, Selection.Person, Selection.SelfEmployed) &&
                      <BCNOptions personPrefix={phnameof(x => x.holder)} />
                    }

                    {isIn(holderType, Selection.Company, Selection.SelfEmployed) &&
                      <ICOOptions personPrefix={phnameof(x => x.holder)} />
                    }

                    <FormGroup>
                      {isIn(holderType, Selection.Person, Selection.SelfEmployed) && <InsureeName personPrefix={phnameof(x => x.holder)} isDisabled={isContracted} />}
                    </FormGroup>

                    <FormSpacer />

                    <InsureeAddressRuian addressPrefix={phnameof(x => x.holder!.address)} isDisabled={isContracted} />
                  </Panel>
                )}

              {/* owner */}
              <FormControl isInvalid={!!errors.policyHolder?.ownerSameAs}>
                <FormLabel tooltip="Zadejte vlastníka vozidla zapsaného v technickém průkazu.">Vlastník vozidla</FormLabel>

                <Controller
                  name='policyHolder.ownerSameAs'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                      <RadioStack variant="stackedTile">
                        {
                          ownerOptions.map(item => (
                            <Radio key={item.value} value={item.value.toString()}>
                              {item.label}
                            </Radio>
                          ))
                        }
                      </RadioStack>
                    </RadioGroup>
                  )}
                />
              </FormControl>

              {!ownerSameAs &&
                (
                  <Panel>
                    <InsureeType personPrefix={phnameof(x => x.owner)} />

                    <FormSpacer />

                    {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                      <CheckboxFormControl name='policyHolder.owner.isVatPayer' control={control} isDisabled={isContracted} label='Plátce DPH' mb={4} />
                    }

                    {isIn(ownerType, Selection.Person, Selection.SelfEmployed) &&
                      <BCNOptions personPrefix={phnameof(x => x.owner)} />
                    }

                    {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                      <ICOOptions personPrefix={phnameof(x => x.owner)} />
                    }

                    <FormGroup>
                      {isIn(ownerType, Selection.Person, Selection.SelfEmployed) && <InsureeName personPrefix={phnameof(x => x.owner)} isDisabled={isContracted} />}
                    </FormGroup>

                    <FormSpacer />

                    <InsureeAddressRuian addressPrefix={phnameof(x => x.owner!.address)} isDisabled={isContracted} />

                    {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                      <FormControl>
                        <FormLabel>
                          Vozidlo na leasing
                        </FormLabel>

                        <Controller
                          name="vehicle.vehicleLeasing"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <RadioGroup onChange={e => onChange(e == '1')} isDisabled={isContracted} value={value ? '1' : '0'}>
                              <RadioStack variant="stackedTile">
                                <Radio value={'1'}>Ano</Radio>
                                <Radio value={'0'}>Ne</Radio>
                              </RadioStack>
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    }
                  </Panel>
                )}

            </FormSectionBody>
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContracted &&
            <FormButtons
              rightButtons={
                <>
                  <Button variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent && isValid}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext && isValid}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}>
                    <Check />
                    Uložit a pokračovat
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={!insuranceParamsId ? 'Nabídka byla uložena do rozpracovaných kalkulací' : 'Nabídka byla úspěšně aktualizována'}
                  />
                </>
              }
            />
          }
        </MainContent>
      </form>
    </FormProvider>
  )
}